'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@repo/ui/lib/utils';

import {
  AWAITING_ALLOCATION_NOTE,
  scoreAwaitsFirstAllocation,
  type HealthAssessment,
  type HealthBand,
  type HealthDriver
} from '../utils/derive-health';
import { signalName, signalStatusLabel } from '../utils/hygiene-presentation';

const BAND_RING_CLASS: Record<HealthBand, string> = {
  strong: 'stroke-rowa-status-positive',
  sound: 'stroke-rowa-status-positive',
  'needs-attention': 'stroke-rowa-status-warning',
  'at-risk': 'stroke-rowa-status-error',
  'insufficient-evidence': 'stroke-rowa-text-primary/15'
};

const BAND_TEXT_CLASS: Record<HealthBand, string> = {
  strong: 'text-rowa-status-positive',
  sound: 'text-rowa-status-positive',
  'needs-attention': 'text-rowa-status-warning',
  'at-risk': 'text-rowa-status-error',
  'insufficient-evidence': 'text-rowa-text-secondary'
};

const RING_RADIUS = 54;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Evidence older than this earns a staleness line; fresher stays silent. */
const STALE_EVIDENCE_MS = 24 * 60 * 60 * 1000;

const SIGNAL_DOT_CLASS: Record<string, string> = {
  ok: 'bg-rowa-status-positive',
  degraded: 'bg-rowa-status-warning'
};

/**
 * The v2 data-confidence strip: one dot per signal — filled green when
 * healthy, amber when degraded, hollow when its evidence source is not
 * wired yet. The sentence lives in the aria-label and per-dot tooltips;
 * the card stays visual. Staleness is the only line that earns words.
 */
export function SignalDots({
  confidence
}: {
  confidence: NonNullable<HealthAssessment['confidence']>;
}) {
  const healthy = confidence.signals.filter(
    (signal) => signal.status === 'ok'
  ).length;
  const unknown = confidence.signals.filter(
    (signal) => signal.status === 'unknown'
  ).length;
  const staleHours =
    confidence.dataAsOf != null
      ? (Date.now() - new Date(confidence.dataAsOf).getTime()) / 3_600_000
      : null;
  const isStale =
    staleHours != null && staleHours * 3_600_000 > STALE_EVIDENCE_MS;

  return (
    <div className="mt-2.5">
      <div
        role="img"
        aria-label={`Data signals: ${healthy} of ${confidence.signals.length} healthy${unknown > 0 ? `, ${unknown} not wired yet` : ''}`}
        className="flex items-center gap-1.5"
      >
        {confidence.signals.map((signal) => (
          <span
            key={signal.id}
            title={`${signalName(signal.id)} · ${signalStatusLabel(signal.status)}`}
            className={cn(
              'size-2 rounded-full',
              SIGNAL_DOT_CLASS[signal.status] ??
                'border-rowa-text-primary/25 border bg-transparent'
            )}
          />
        ))}
      </div>
      {isStale ? (
        <p className="text-rowa-label-sm text-rowa-status-warning mt-1">
          Data is about {Math.round(staleHours!)} hours behind.
        </p>
      ) : null}
    </div>
  );
}

export function HealthRing({ assessment }: { assessment: HealthAssessment }) {
  const reducedMotion = useReducedMotion();
  const hasScore = assessment.score != null;
  const coveragePct = Math.round(assessment.coverageBps / 100);
  // No score yet: the ring shows progress toward one — evidence coverage as
  // a quiet arc — instead of an alarmed empty state. Absence stays calm.
  const progress = hasScore ? assessment.score! / 100 : coveragePct / 100;
  const targetOffset = RING_CIRCUMFERENCE * (1 - progress);

  return (
    <div
      className="flex flex-col items-center"
      role="img"
      aria-label={
        hasScore
          ? `Portfolio health score ${assessment.score} of 100, ${assessment.bandLabel.toLowerCase()}${assessment.provisional ? ', early score based on partial data' : ''}`
          : scoreAwaitsFirstAllocation(assessment)
            ? `Portfolio health not yet scored. ${AWAITING_ALLOCATION_NOTE}`
            : `Portfolio health not yet scored; ${coveragePct} percent of the data is in`
      }
    >
      <div className="relative size-[126px] shrink-0">
        <svg className="size-full -rotate-90" viewBox="0 0 126 126" aria-hidden>
          <circle
            className="stroke-rowa-text-primary/10"
            cx="63"
            cy="63"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="9"
            strokeDasharray={hasScore ? undefined : '3 8'}
            strokeLinecap="round"
          />
          {progress > 0 ? (
            <motion.circle
              className={
                hasScore
                  ? BAND_RING_CLASS[assessment.band]
                  : 'stroke-rowa-status-positive/40'
              }
              cx="63"
              cy="63"
              r={RING_RADIUS}
              fill="none"
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              initial={
                reducedMotion
                  ? { strokeDashoffset: targetOffset }
                  : { strokeDashoffset: RING_CIRCUMFERENCE }
              }
              animate={{ strokeDashoffset: targetOffset }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            />
          ) : null}
        </svg>
        <span
          className="text-rowa-data-hero text-rowa-text-primary absolute inset-0 flex items-center justify-center tabular-nums"
          aria-hidden
        >
          {hasScore ? assessment.score : `${coveragePct}%`}
        </span>
      </div>
      {/* Outside the ring on purpose: the longest band ("NEEDS ATTENTION")
          is wider than the ring's inner circle, so centring it inside ran
          the text straight through the stroke. */}
      <span
        className={cn(
          'text-rowa-label-sm mt-2 text-center tracking-[0.1em]',
          hasScore
            ? BAND_TEXT_CLASS[assessment.band]
            : 'text-rowa-text-tertiary'
        )}
        aria-hidden
      >
        {hasScore ? assessment.bandLabel : 'DATA'}
      </span>
    </div>
  );
}

const MINI_RADIUS = 30;
const MINI_CIRCUMFERENCE = 2 * Math.PI * MINI_RADIUS;

/** A small band-colored score ring — same visual language as the hero. */
export function MiniRing({ driver }: { driver: HealthDriver }) {
  const hasScore = driver.score != null;
  const offset = MINI_CIRCUMFERENCE * (1 - (driver.score ?? 0) / 100);

  return (
    <div className="relative size-[72px] shrink-0" aria-hidden>
      <svg className="size-full -rotate-90" viewBox="0 0 72 72">
        <circle
          className="stroke-rowa-text-primary/10"
          cx="36"
          cy="36"
          r={MINI_RADIUS}
          fill="none"
          strokeWidth="6"
          strokeDasharray={hasScore ? undefined : '2.5 6'}
          strokeLinecap="round"
        />
        {hasScore ? (
          <circle
            className={cn(
              driver.state === 'at-risk'
                ? 'stroke-rowa-status-error'
                : driver.state === 'watch'
                  ? 'stroke-rowa-status-warning'
                  : driver.state === 'sound'
                    ? 'stroke-rowa-status-positive'
                    : // A score in a band this build does not know: neutral.
                      'stroke-rowa-text-primary/40',
              'motion-safe:transition-[stroke-dashoffset] motion-safe:duration-700'
            )}
            cx="36"
            cy="36"
            r={MINI_RADIUS}
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={MINI_CIRCUMFERENCE}
            strokeDashoffset={offset}
          />
        ) : null}
      </svg>
      <span className="text-rowa-data-medium text-rowa-text-primary absolute inset-0 flex items-center justify-center tabular-nums">
        {driver.score ?? '—'}
      </span>
    </div>
  );
}
